import type { ErrorLike } from '@apollo/client'
import {
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  ServerError,
  ServerParseError,
} from '@apollo/client/errors'
import { commonMessages } from '@common/intl'
import { AuthScopeCode } from '@gql'
import { getValidationErrors } from '@hooks/useServerFormHooks'
import type { GraphQLFormattedError } from 'graphql'
import type { IntlShape } from 'react-intl'

export function isJwtError(error: GraphQLFormattedError): boolean {
  const JWTErrors = [AuthScopeCode.JWT_EXPIRED, AuthScopeCode.JWT_INVALID]
  return JWTErrors.includes(error.extensions?.scopeCode as AuthScopeCode)
}

export function isTokenExpired(error: GraphQLFormattedError): boolean {
  return error.extensions?.scopeCode === AuthScopeCode.JWT_EXPIRED
}

export function isForbiddenError(error: GraphQLFormattedError): boolean {
  return error.extensions?.code === 'FORBIDDEN'
}

export function hasAnyForbiddenError(graphQLErrors: readonly GraphQLFormattedError[]) {
  return graphQLErrors?.some(isForbiddenError)
}

export function shouldRefreshJwtToken(graphQLErrors?: readonly GraphQLFormattedError[]) {
  if (graphQLErrors?.some(isTokenExpired)) {
    return true
  }

  return false
}

export enum GraphQLErrorCode {
  InputValidationFailed = 'INPUT_VALIDATION_FAILED',
  InternalServerError = 'INTERNAL_SERVER_ERROR',
}

export function getErrorMessage(intl: IntlShape, error: Error): string {
  if (ServerError.is(error) && ServerParseError.is(error) && CombinedProtocolErrors.is(error)) {
    return intl.formatMessage(commonMessages.noConnectionToServerError)
  }

  if (error instanceof CombinedGraphQLErrors) {
    const errorMessage = getValidationErrors(error.errors, null)[0]?.message

    if (errorMessage) {
      return errorMessage
    }

    if (error.errors.some((e) => e.extensions?.code === GraphQLErrorCode.InternalServerError)) {
      return intl.formatMessage(commonMessages.internalServerError)
    }
  }

  return intl.formatMessage(commonMessages.somethingWentWrong)
}

export function extractGraphqlErrors(error: ErrorLike | undefined) {
  if (error && CombinedGraphQLErrors.is(error)) {
    return error.errors
  }

  return []
}
